import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload } from "lucide-react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";

export default function RingRequestForm({ open, onClose, promo }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", details: "", budget_min: "", budget_max: "", budget_range: "", inspiration_note: "" });
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const inp = "w-full h-12 px-4 border bg-transparent outline-none text-sm mb-3 focus:border-[var(--ev-text)]";

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ring-request/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } catch (err) { toast.error("Upload failed. Please try again."); }
    setUploading(false);
  };

  const submit = async () => {
    if (!f.name || !f.email || !f.phone || !f.details) return toast.error("Please complete all required fields.");
    if (f.budget_min && f.budget_max && Number(f.budget_min) > Number(f.budget_max)) return toast.error("Minimum budget cannot exceed maximum budget.");
    setSubmitting(true);
    try {
      await api.post("/ring-request", {
        name: f.name, email: f.email, phone: f.phone, details: f.details,
        budget_min: f.budget_min ? Number(f.budget_min) : null, budget_max: f.budget_max ? Number(f.budget_max) : null,
        budget_range: f.budget_range || null, inspiration_image: image, inspiration_note: f.inspiration_note || null,
      });
      setDone(true);
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/50 flex justify-center overflow-y-auto py-10 px-4" onClick={onClose} data-testid="ring-request-modal">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-xl h-fit p-8 md:p-10" style={{ background: "var(--ev-surface)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="eyebrow mb-2" style={{ color: "var(--ev-muted)" }}>Bespoke Atelier</div>
                <h2 className="font-serif text-3xl">Create Your Bespoke Ring</h2>
              </div>
              <button data-testid="ring-form-close" onClick={onClose}><X size={24} strokeWidth={1} /></button>
            </div>

            {done ? (
              <div className="text-center py-10" data-testid="ring-form-success">
                <p className="font-serif text-2xl mb-4">{promo?.confirmation || "Thank you for sharing your vision with Maison Évaren. Our team looks forward to helping you create something extraordinary."}</p>
                <p className="text-sm mb-8" style={{ color: "var(--ev-muted)" }}>Our team will get back to you within 2–3 working days.</p>
                <button onClick={onClose} className="btn-luxe">Close</button>
              </div>
            ) : (
              <>
                <input data-testid="ring-name" placeholder="Full name *" className={inp} style={{ borderColor: "var(--ev-border)" }} value={f.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="ring-email" type="email" placeholder="Email address *" className={inp} style={{ borderColor: "var(--ev-border)" }} value={f.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="ring-phone" placeholder="Phone number *" className={inp} style={{ borderColor: "var(--ev-border)" }} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
                <label className="eyebrow block mb-1 mt-2">Tell Us About Your Dream Ring *</label>
                <textarea data-testid="ring-details" className={`${inp} h-28 py-3`} style={{ borderColor: "var(--ev-border)" }} value={f.details} onChange={(e) => set("details", e.target.value)}
                  placeholder="Design preferences, materials, gemstones, setting styles, engraving…" />
                <label className="eyebrow block mb-1 mt-2">Your Preferred Budget (optional)</label>
                {promo?.budget_ranges?.length ? (
                  <select className={inp} style={{ borderColor: "var(--ev-border)" }} value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)}>
                    <option value="">Select a range</option>
                    {promo.budget_ranges.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" placeholder="Min" className={inp} style={{ borderColor: "var(--ev-border)" }} value={f.budget_min} onChange={(e) => set("budget_min", e.target.value)} />
                    <input type="number" placeholder="Max" className={inp} style={{ borderColor: "var(--ev-border)" }} value={f.budget_max} onChange={(e) => set("budget_max", e.target.value)} />
                  </div>
                )}
                <label className="eyebrow block mb-1 mt-2">Upload Your Inspiration (optional)</label>
                <p className="text-xs mb-2" style={{ color: "var(--ev-muted)" }}>Have a design in mind? Share an image that inspires you.</p>
                {image ? (
                  <div className="relative w-32 h-32 mb-3">
                    <img src={image} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-neutral-900 text-white rounded-full w-6 h-6">×</button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 border border-dashed px-4 h-12 mb-3 cursor-pointer text-sm" style={{ borderColor: "var(--ev-border)" }}>
                    <Upload size={16} /> {uploading ? "Uploading…" : "Choose an image (JPG/PNG/WebP)"}
                    <input data-testid="ring-image" type="file" accept="image/*" className="hidden" onChange={upload} />
                  </label>
                )}
                <label className="eyebrow block mb-1 mt-2">What Do You Love About This Design? (optional)</label>
                <textarea className={`${inp} h-20 py-3`} style={{ borderColor: "var(--ev-border)" }} value={f.inspiration_note} onChange={(e) => set("inspiration_note", e.target.value)} />
                <button data-testid="ring-submit" disabled={submitting} onClick={submit} className="btn-luxe w-full mt-3">
                  {submitting ? "Submitting…" : "Submit My Ring Request"}
                </button>
                <p className="text-xs text-center mt-4" style={{ color: "var(--ev-muted)" }}>Our team will get back to you within 2–3 working days.</p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
